import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Gift, Copy, Share2, Users } from "lucide-react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Card } from "@/components/Layout";

export const ReferralCard = () => {
  const { t } = useApp();
  const [d, setD] = useState(null);
  const [code, setCode] = useState(localStorage.getItem("ks_ref") || "");
  const load = useCallback(() => api.get("/referrals").then((r) => setD(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return null;
  const link = `${window.location.origin}/?ref=${d.code}`;
  const msg = `${t("Give your dry scrap to Kindsaver and get paid. Use my referral code")} ${d.code}: ${link}`;
  const apply = async () => {
    try { await api.post("/referrals/apply", { code }); localStorage.removeItem("ks_ref"); toast.success(t("Referral code added")); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card className="space-y-4" data-testid="referral-card">
      <h2 className="flex items-center gap-2 font-display text-lg font-bold text-[#0D3B2B]"><Gift className="h-5 w-5 text-[#E05C3A]" />{t("Refer & earn")}</h2>
      <p className="text-sm text-[#4A6358]">{t("Share your code. When a friend's first pickup is completed, you get")} {d.percent}% {t("of that pickup's amount.")}</p>
      <div className="flex items-center justify-between rounded-2xl border-2 border-dashed border-[#2E9E6B] px-4 py-3">
        <span className="font-display text-2xl font-extrabold tracking-wide text-[#0D3B2B]" data-testid="referral-code">{d.code}</span>
        <button onClick={() => { navigator.clipboard?.writeText(link); toast.success(t("Link copied")); }} data-testid="referral-copy-btn" className="flex items-center gap-1 text-sm font-semibold text-[#2E9E6B]"><Copy className="h-4 w-4" />{t("Copy")}</button>
      </div>
      <a href={`https://wa.me/?text=${encodeURIComponent(msg)}`} target="_blank" rel="noreferrer" data-testid="referral-whatsapp-btn"
        className="flex h-12 items-center justify-center gap-2 rounded-2xl bg-[#25D366] font-bold text-white"><Share2 className="h-5 w-5" />{t("Share on WhatsApp")}</a>
      <div className="grid grid-cols-3 gap-2 text-center">
        {[["Referred", d.referrals.length, "count"], ["Earned", `₹${d.earned}`, "earned"], ["Pending", `₹${d.pending}`, "pending"]].map(([l, v, k]) => (
          <div key={k} className="rounded-2xl bg-[#F4F8F5] p-3"><p className="font-display text-xl font-extrabold text-[#0D3B2B]" data-testid={`referral-${k}`}>{v}</p><p className="text-xs text-[#4A6358]">{t(l)}</p></div>
        ))}
      </div>
      {d.referrals.length > 0 && (
        <div className="space-y-2" data-testid="referral-list">
          <p className="flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-[#7B968B]"><Users className="h-3.5 w-3.5" />{t("My referrals")}</p>
          {d.referrals.map((r, i) => (
            <div key={i} className="flex items-center justify-between rounded-xl bg-[#F4F8F5] px-3 py-2 text-sm" data-testid={`referral-row-${i}`}>
              <span><b>{r.name}</b> <span className="text-xs text-[#7B968B]">· {t(r.customer_type)} · {r.joined ? fmtDate(r.joined.slice(0, 10)) : ""}</span><br /><span className="text-xs">{t(r.status)}</span></span>
              <span className="text-right text-sm font-bold">{r.reward != null ? `₹${r.reward}` : "—"}{r.reward_status && <span className={`block text-[11px] ${r.reward_status === "Paid" ? "text-[#2E9E6B]" : "text-[#E05C3A]"}`}>{t(r.reward_status)}</span>}</span>
            </div>
          ))}
        </div>
      )}
      {d.referred_by && <p className="text-xs text-[#7B968B]" data-testid="referral-referred-by">{t("You joined with a code from")} {d.referred_by}</p>}
      {d.can_apply && (
        <div className="flex gap-2">
          <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder={t("Have a referral code?")} data-testid="referral-apply-input"
            className="h-12 min-w-0 flex-1 rounded-xl border border-[#D2E0D8] px-3 text-base" />
          <button onClick={apply} disabled={!code.trim()} className="h-12 rounded-xl bg-[#0D3B2B] px-4 font-semibold text-white disabled:opacity-40" data-testid="referral-apply-btn">{t("Apply")}</button>
        </div>
      )}
    </Card>
  );
};
